import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';

import {
  BASIC_MARGIN,
  COL_PADDING_OFFSET,
  WITHOUT_BUTTON_FOCUS_STYLING,
} from 'analyses/alignment/modals/StyleConstants';
import {ParameterOptionValue, ParameterOptionValues} from 'constants/AlignmentConstants';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

/**
 * Component that renders a parameter value selection dropdown field populated with the current value.
 * @param props Component props.
 * @param props.label The fields display label.
 * @param props.name The name of the parameter.
 * @param props.options The selectable options in the dropdown.
 * @param props.currentValue The value of the currently selected option.
 * @param props.updateParams Callback for updating
 * @constructor
 */
function ProgramParamsSelectDropdown<T extends {toString(): string}>({
  label,
  name,
  options,
  currentValue,
  updateParams,
}: {
  label: string;
  name: string;
  options: Array<ParameterOptionValue<T>>;
  currentValue: T;
  updateParams: (params: {[p: string]: T}) => void;
}): JSX.Element {
  const currentOption = options.find((option) => option.value === currentValue)!;
  const menuOptions = options.map((parameterOption) => (
    <Option
      onSelect={(value: T): void => {
        updateParams({[name]: value});
      }}
      value={parameterOption.value}
      metadata={parameterOption.metadata}
      key={parameterOption.value.toString()}
    >
      {parameterOption.display ?? parameterOption.value?.toString()}
    </Option>
  ));
  return (
    <Col lg={6} md={6} sm={6} xs={6} className={css(styles.param)} key={name}>
      <FieldLabel label={label} />
      <SelectDropdown
        displayName={currentOption.display ?? currentOption.value?.toString()}
        menu={
          <Menu value={currentValue} scrollable={true}>
            {menuOptions}
          </Menu>
        }
      />
    </Col>
  );
}

/**
 * Component that renders a group of parameter selection dropdown fields and a reset button for those fields.
 * @param props Component props.
 * @param props.currentParameters Currently selected values for the parameters.
 * @param props.parameterNames Names of the parameters to display dropdowns for.
 * @param props.parameterOptionValues Object containing the selectable values for each parameter.
 * @param props.updateParams Callback for updating params.
 * @param props.defaultParameters Default parameter values to use when the reset button is clicked.
 * @constructor
 */
export default function ProgramParamsSection<T extends {}>({
  currentParameters,
  parameterNames,
  parameterOptionValues,
  updateParams,
  defaultParameters,
}: {
  currentParameters: T;
  parameterNames: Array<Extract<keyof T, string>>;
  parameterOptionValues: ParameterOptionValues<T>;
  defaultParameters: T;
  updateParams: (params: {[param in Extract<keyof T, string>]: T[param]}) => void;
}): JSX.Element {
  const dropdowns = parameterNames.map((name) => {
    const parameterOptions = parameterOptionValues[name];
    return (
      <ProgramParamsSelectDropdown
        key={name}
        label={parameterOptions.label}
        name={name}
        // @ts-expect-error: Fixme (Typescript 4.8 upgrade)
        // Error:
        // Type 'ParameterOptionValue<T[Extract<keyof T, string>]>[]' is not assignable to type 'ParameterOptionValue<{ toString(): string; }>[]'.
        //   Type 'ParameterOptionValue<T[Extract<keyof T, string>]>' is not assignable to type 'ParameterOptionValue<{ toString(): string; }>'.
        //     Type 'T[Extract<keyof T, string>]' is not assignable to type '{ toString(): string; }'.
        //       Type 'T[string]' is not assignable to type '{ toString(): string; }'.ts(2322)
        options={parameterOptions.options}
        // @ts-expect-error: Fixme (Typescript 4.8 upgrade)
        // Error: Type 'T[Extract<keyof T, string>]' is not assignable to type '{ toString(): string; }'.
        currentValue={currentParameters[name]}
        // @ts-expect-error: Fixme (Typescript 4.8 upgrade)
        // Error:
        // Type '(params: { [param in Extract<keyof T, string>]: T[param]; }) => void' is not assignable to type '(params: { [p: string]: { toString(): string; }; }) => void'.
        //   Types of parameters 'params' and 'params' are incompatible.
        //     Type '{ [p: string]: { toString(): string; }; }' is not assignable to type '{ [param in Extract<keyof T, string>]: T[param]; }'.ts(2322)
        updateParams={updateParams}
      />
    );
  });
  const onReset = (): void => {
    updateParams(defaultParameters);
  };

  return (
    <>
      <div className={classNames('u-flex-display', css(styles.colPaddingOffset))}>
        <FormGroup className="u-flex-grow u-no-spacing-bottom">{dropdowns}</FormGroup>
      </div>
      <Button
        buttonStyle={ButtonStyle.LINK}
        text="Reset to defaults"
        className={classNames('u-moderate-spacing-bottom', css(styles.withoutButtonFocusStyling))}
        onClick={onReset}
        disabled={isEqual(currentParameters, defaultParameters)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  param: {marginBottom: BASIC_MARGIN},
  colPaddingOffset: COL_PADDING_OFFSET,
  withoutButtonFocusStyling: WITHOUT_BUTTON_FOCUS_STYLING,
});
